/**
 * The checkpoint — D142 (`docs/design/the-descent-arc.md` §5; Mike's ruling:
 * death returns to a checkpoint).
 *
 * @remarks
 * Two halves. `takeCheckpoint` is the `checkpoint` effect: a photograph of
 * the world, the clock, and the narrative ledgers, kept in progression.
 * `restoreCheckpoint` is what a `restartAt: 'checkpoint'` ending does once
 * its epilogue is spoken, and it follows ONE rule:
 *
 * **The checkpoint restores the world, not the player.** World flags, doors,
 * switches, unlocked and revealed exits, the scheduler's clock and the
 * world's memory of which triggers have fired — all go back to the
 * photograph. The player's HANDS do not: what is carried stays carried, what
 * was taken stays taken, what was dropped stays where it was dropped, and
 * the acquisition record keeps its history. Calder's content fixes the rule
 * from both sides — the film strip is "carried across the checkpoint, never
 * explained," and the player who reaches Exposure holding the mask is the one
 * who had the answer in their hands and did not put it on; she puts them back,
 * and the mask is still in their hands. The rooms they walked stay on the map,
 * the transcript keeps everything that was said, and time does not run
 * backward: the turn count continues, so every entry the transcript will ever
 * carry stays in order, and the photograph's pending events are re-timed
 * relative to now rather than resurrected into the past.
 *
 * D146 draws the same line through the rest (Mike's ruling: once spoken,
 * stay spent; Calder's finding: the film's frame, once seen, cannot be
 * un-known). **What the player has learned and what she has said are the
 * player's:** a `scope: 'player'` flag keeps its value, the documents read
 * stay read, her voice ledger keeps every line she has spoken and (D231) so
 * does the ledger of `once` topics — 8a's "you
 * will not remember agreeing to this" stays an asymmetry she does not
 * disprove one turn later — and the occurrence tallies keep counting,
 * because they count what HAPPENED and the turn count already says that
 * time did not run backward. What fires again is the world's: the trigger
 * ledger goes back, so the clock re-arms on the next descent, and the
 * ending is cleared.
 *
 * **A checkpoint never holds the death it returns from (D225, Calder's E2).**
 * The room sugar re-takes the photograph on every executed turn in the room
 * — "the room as the player last left it" — and the POC's tester, gassed in
 * PATHOLOGY 2, fled back up to the substation with the purge one tick from
 * killing them. The arrival re-took the photograph with the kill on its
 * clock; the `look` that died re-took it again (step 7 fires the room's
 * trigger before step 7b settles the deadline), with the kill due that very
 * turn; and the restore re-timed a clock that already held the death, so the
 * next tick killed again, restored again, and every command for the rest of
 * the session replayed "SUBJECT RECOVERED". The design's own words say what
 * a checkpoint is for: the countdown there is "not started, which is the
 * point." So the photograph is REFUSED while the clock holds an occurrence
 * whose firing would select an ending that returns to a checkpoint, and
 * refused of a world a terminal ending has already closed (an event that
 * fires at the tick kills BEFORE step 7 photographs the room, so the dying
 * turn would otherwise photograph its own death) — the earlier photograph
 * stands, the one taken before the clock started — and a restore drops any
 * such occurrence a photograph made before this rule still carries (a save
 * in flight on the demo), so no restored world fires the ending that sent
 * the player back on its own.
 *
 * @packageDocumentation
 */

import {
	type CheckpointState,
	type CounterId,
	type EffectDefinition,
	effectsWithin,
	type EndingDefinition,
	type FlagId,
	type ItemId,
	type PendingScheduledEvent,
	type RoomId,
	type RuntimeState,
	type RuntimeTranscriptEntry,
} from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { TranscriptCursor } from './transcript-cursor.js';

import { requireCounter } from '../bundle/bundle-lookups.js';
import { followingStepOf, withFollowersArrived } from '../entities/actor-following.js';
import { terminalEndingOf } from '../narrative/session-ended.js';
import { enterRoom } from './enter-room.js';

/**
 * Would this pending occurrence, when it fires, select an ending that returns
 * the player to a checkpoint (D225)?
 *
 * @remarks
 * Bundle data only: the event's declared effects, and the `restartAt` of any
 * ending they select. The occurrence may still be averted — the POC's purge
 * is conditioned on the mask being off — but a world in which it is pending
 * is a world already in peril, and a photograph of peril is no place to come
 * back to. An event the bundle does not define cannot have been scheduled;
 * the lookup's miss is answered `false` because the loader, not this walk,
 * is where an undefined event is refused.
 *
 * D246: every arm of every branch counts, whichever the world would choose
 * when the event fires — the question is what the firing COULD do, and the
 * state that decides the branch is not yet the state it will be read in.
 *
 * @param bundle - The loaded bundle
 * @param pending - One occurrence on the clock
 * @returns Whether its firing would send the player back to a checkpoint
 */
const returnsToACheckpoint = (bundle: EngineGameBundle, pending: PendingScheduledEvent): boolean =>
	effectsWithin(bundle.indexes.eventsById.get(pending.eventDefinitionId)?.effects ?? []).some((effect) => selectsAReturningEnding(bundle, effect));

/**
 * Would this one effect select an ending that returns to a checkpoint —
 * itself, or (D244) through the edge of a counter it moves toward?
 *
 * @remarks
 * Act II's air is a tick that spends the suit a minute at a time, and the
 * death is the suit counter's `onMin`, not the tick's own effect — so a
 * clock holding the tick holds the death as surely as the POC's purge held
 * its own, and D225's rule must see through the counter to refuse the
 * photograph. A move DOWN can reach the floor, a move UP the ceiling, and a
 * setting reaches whichever edge it names; the edge's effects are read one
 * level deep, because the loader refuses an edge whose effects could cross
 * another — and (D246) through every arm of a branch among them: the
 * thirteenth-minute death that chooses its ending by room still holds the
 * photograph.
 *
 * @param bundle - The loaded bundle
 * @param effect - One effect of a pending event
 * @returns Whether applying it could send the player back to a checkpoint
 */
const selectsAReturningEnding = (bundle: EngineGameBundle, effect: EffectDefinition): boolean => {
	if (effect.type === 'set_ending') {
		return bundle.indexes.endingsById.get(effect.endingId)?.restartAt === 'checkpoint';
	}

	if (effect.type !== 'adjust_counter' && effect.type !== 'set_counter') {
		return false;
	}

	const counter = requireCounter(bundle, effect.counterId);
	const edgeApproached = effect.type === 'adjust_counter' ? (effect.by < 0 ? counter.min : counter.max) : effect.value;
	const edgeEffects = edgeApproached === counter.min ? counter.onMin : edgeApproached === counter.max ? counter.onMax : [];

	return effectsWithin(edgeEffects).some((edgeEffect) => edgeEffect.type === 'set_ending' && selectsAReturningEnding(bundle, edgeEffect));
};

/**
 * Photograph the world as it stands — the `checkpoint` effect. Effects run
 * pre-commit, so the turn being committed is `turnCount + 1`.
 *
 * @remarks
 * D225: no photograph is taken of a world whose clock already holds the
 * death that would return to it, nor of a world a terminal ending has closed
 * (see the module remarks) — the state comes back unchanged, and the
 * photograph taken before the clock started stands.
 *
 * @param bundle - The loaded bundle (the clock's events and their endings)
 * @param state - The state at the moment of the effect
 * @param wakeRoomId - D270: where a restore wakes the player, when not here
 * @returns The state carrying the new checkpoint, or the same state when it is a death
 */
export const takeCheckpoint = (bundle: EngineGameBundle, state: RuntimeState, wakeRoomId?: RoomId): RuntimeState => {
	if (terminalEndingOf(bundle, state) !== undefined || state.scheduler.pendingEvents.some((pending) => returnsToACheckpoint(bundle, pending))) {
		return state;
	}

	// D270: where the restore wakes the player — the room it names, or this one. A photograph that wakes them elsewhere
	// carries the company the way an arrival does: the followers who would walk with them there stand there in it, so the
	// return (which moves no body, because the photograph already says where every body stands) finds them together.
	const wakesIn = wakeRoomId ?? state.progression.activeRoomId;
	const checkpoint: CheckpointState = {
		activeRoomId: wakesIn,
		narrative: state.narrative,
		scheduler: state.scheduler,
		takenAtTurn: state.progression.turnCount + 1,
		world:
			wakesIn === state.progression.activeRoomId
				? state.world
				: { ...state.world, actorLocations: withFollowersArrived(state.world.actorLocations, followingStepOf(bundle, state, wakesIn).arriving, wakesIn) },
	};

	return { ...state, progression: { ...state.progression, checkpoint }};
};

/** What restoring did: the state put back, and the arrival lines spoken. */
export interface CheckpointRestoration {
	readonly cursor: TranscriptCursor;
	readonly entries: readonly RuntimeTranscriptEntry[];
	readonly state: RuntimeState;
}

/**
 * Put the player back at the checkpoint after a `restartAt: 'checkpoint'`
 * ending's epilogue — the world restored, the hands kept, the ending cleared,
 * and the checkpoint room entered again (its brief form, since it has been
 * seen) under the `checkpoint_restored:<endingId>` context.
 *
 * @param bundle - The loaded bundle
 * @param state - The state with the ending selected and its epilogue spoken
 * @param ending - The ending that sent the player back
 * @param cursor - Where the arrival lands
 * @returns The restoration, or the state untouched when no checkpoint was ever taken
 */
export const restoreCheckpoint = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	ending: EndingDefinition,
	cursor: TranscriptCursor,
): CheckpointRestoration => {
	const { checkpoint } = state.progression;

	// No photograph to return to: the ending stands as the terminal ending it
	// is. The loader refuses a pack where this can happen (a restartAt ending
	// with no checkpoint source), so this is the honest fallback, not a path.
	if (checkpoint === undefined) {
		return { cursor, entries: [], state };
	}

	const nowTurn = state.progression.turnCount + 1;
	const turnShift = nowTurn - checkpoint.takenAtTurn;
	const restored: RuntimeState = {
		...state,
		narrative: {
			// D235 (ruling 4): what an actor has asked stays asked — the
			// occupant does not ask "Is she with you?" twice because the
			// player died once.
			askedPromptIds: state.narrative.askedPromptIds,
			// The world's memory of what has fired goes back — and so does the
			// ending history (D151): an OPEN ending selected before the
			// photograph was part of the world then and is again; anything
			// selected after it — the terminal ending that sent the player
			// here — is gone with the rest of the world, which is what makes
			// the session never end.
			firedTriggerIds: checkpoint.narrative.firedTriggerIds,
			// What the player has read, what she has said, and what has
			// happened are the player's — kept (D146).
			occurrenceTallies: state.narrative.occurrenceTallies,
			readLogIds: state.narrative.readLogIds,
			selectedEndingIds: checkpoint.narrative.selectedEndingIds,
			// D231 (Mike's ruling 4): what has been said once stays said — a
			// `once` topic is the player's, exactly as her voice ledger is.
			spentTopicIds: state.narrative.spentTopicIds,
			voiceLineLastSpokenTurn: state.narrative.voiceLineLastSpokenTurn,
			voiceLineUsage: state.narrative.voiceLineUsage,
		},
		progression: { ...state.progression, activeRoomId: checkpoint.activeRoomId },
		scheduler: {
			...checkpoint.scheduler,
			// The photograph's clock, re-timed: an event that was N turns away
			// when the checkpoint was taken is N turns away again now. D225: an
			// occurrence that would select a returning ending never comes back
			// — `takeCheckpoint` refuses such a photograph now, and a photograph
			// made before it learned to (a save in flight) is read by the same
			// rule here, so a restored world cannot die on its own next tick.
			pendingEvents: checkpoint.scheduler.pendingEvents
				.filter((pending) => !returnsToACheckpoint(bundle, pending))
				.map((pending) => ({ ...pending, dueTurn: pending.dueTurn + turnShift })),
			sequenceCounter: state.scheduler.sequenceCounter,
		},
		world: {
			...checkpoint.world,
			// The hands, and their history, are the player's — kept.
			acquisitions: state.world.acquisitions,
			// D244: an amount is whose fact its counter says — `scope: 'player'`
			// keeps the live value past the restore; `'world'` goes back to the
			// photograph, the canister in the rack there to be taken again.
			counterValues: withPlayersKept(checkpoint.world.counterValues, state.world.counterValues, (counterId) =>
				bundle.indexes.counterDefinitionsById.get(counterId as CounterId)?.scope === 'player',
			),
			droppedItemLocations: state.world.droppedItemLocations,
			// Worn is a fact about a carried thing (D143) — the mask a player
			// died wearing is still on their face. Missed by D143, caught by D146.
			equippedItemIds: state.world.equippedItemIds,
			// D146: a `scope: 'player'` flag is a fact the player has learned,
			// and it is kept with the hands; the world's flags go back.
			flagValues: withPlayersKept(checkpoint.world.flagValues, state.world.flagValues, (flagId) =>
				bundle.indexes.flagDefinitionsById.get(flagId as FlagId)?.scope === 'player',
			),
			inventoryItemIds: state.world.inventoryItemIds,
			removedRoomItemIds: state.world.removedRoomItemIds,
			// D152: a strap's wear is whose fact its pack says — `scope: 'player'`
			// stays broken past the restore; `'world'` is whole again.
			spentUses: withPlayersKept(checkpoint.world.spentUses, state.world.spentUses, (itemId) =>
				bundle.indexes.itemsById.get(itemId as ItemId)?.durability?.scope === 'player',
			),
		},
	};
	const arrival = enterRoom(bundle, restored, checkpoint.activeRoomId, cursor);

	return {
		cursor: arrival.cursor,
		entries: arrival.entries.map((entry) => ({ ...entry, triggerContext: `checkpoint_restored:${ending.endingId}` })),
		state: arrival.state,
	};
};

/**
 * The photograph's record with every PLAYER-scoped entry overwritten by its
 * live value — the world goes back, what the player carries does not (D146
 * for flags; D152 for wear).
 *
 * @param photographed - The checkpoint's record
 * @param live - The record at the ending
 * @param isPlayers - Whether an entry's key names a `scope: 'player'` fact
 * @returns The restored record
 */
const withPlayersKept = <Value>(
	photographed: Readonly<Record<string, Value>>,
	live: Readonly<Record<string, Value>>,
	isPlayers: (key: string) => boolean,
): Readonly<Record<string, Value>> => ({
	...photographed,
	...Object.fromEntries(Object.entries(live).filter(([key]) => isPlayers(key))),
});
